//Gallery/FollowingMobile.tsx

import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import {PostSection} from "./GalleryUIMobile";
import { useTranslation } from 'react-i18next';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { FaBars } from 'react-icons/fa';
import type {MediaEntry} from "./GalleryUIMobile";

type PostType = 'media';
 
interface Post {
  _id: string;
  type: PostType;
  captions: string[];
  title?: string;
  titleColor?: string;
  media: MediaEntry[];     
  createdAt: string;
location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
    likes: number;
    totalLikes?: number;
    likeKey?: string;
    likedByUser?: boolean;
user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;}


  interface LiteUser {
  _id: string;
  profilename: string;
  picture?: string;
}

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}      
// ---- Asset prefetch utilities ----
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm)$/i;
const isVideoUrl = (u: string) => {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return VIDEO_EXT_RE.test(pathname);
  } catch {
    return VIDEO_EXT_RE.test(u);
  }
};

const IMAGE_BASE = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_BASE) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const toImageUrl = (p: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const base = IMAGE_BASE.replace(/\/$/, '');
  const rel  = p.replace(/^\//, '');
  return `${base}/${rel}`;
};

// Idle scheduler so we don't block the main thread
const scheduleIdle = (fn: () => void) => {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn, { timeout: 1000 });
  } else {
    setTimeout(fn, 0);
  }
};

// How aggressively we prefetch
const PREFETCH_POSTS_AHEAD = 10;          // ← preload ~5 posts ahead
const PREFETCH_MEDIA_PER_POST = 5;       // ← first N media per post (tune if needed)

// Limiting Memory
const WINDOW_BEFORE = 1;
const WINDOW_AFTER = 2;
const MAX_POSTS_IN_MEMORY = 60;


// Optional: keep image handles bounded to avoid unbounded memory
const MAX_IMAGE_HANDLES = 300;
const trimImageHandles = () => {
  if (liveImageHandles.size <= MAX_IMAGE_HANDLES) return;
  // drop oldest ~50
  const drop = Math.min(50, liveImageHandles.size);
  const it = liveImageHandles.keys();
  for (let i = 0; i < drop; i++) {
    const k = it.next().value;
    if (k) liveImageHandles.delete(k);
  }
};

// Light network heuristic: don't prefetch on very slow/expensive links
const canPrefetch = () => {
  const nav = (navigator as any);
  const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
  const saveData = !!conn?.saveData;
  const et: string | undefined = conn?.effectiveType;
  // treat '2g', 'slow-2g' as too slow
  const verySlow = et === 'slow-2g' || et === '2g';
  return !saveData && !verySlow;
};

// In-memory cache so we don't prefetch the same URL repeatedly
const prefetched = new Set<string>();

/**
 * Prefetch an image/video. For images we keep a reference to avoid premature GC.
 * For videos we create a metadata-only video element.
 */
const liveImageHandles = new Map<string, HTMLImageElement>();

const prefetchAsset = (url?: string) => {
  if (!url || prefetched.has(url) || !canPrefetch()) return;

  if (isVideoUrl(url)) {
    const v = document.createElement('video');
    v.preload = 'metadata';        // keep it light
    v.src = url;
    // Kick load on iOS/Safari
    try { v.load(); } catch {}
  } else {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
    liveImageHandles.set(url, img);
    // When it settles, we can drop the handle
    const cleanup = () => liveImageHandles.delete(url);
    img.onload = cleanup;
    img.onerror = cleanup;
  }

  // Optional <link rel="prefetch"> to hint the browser (mostly for images)
  try {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
    // don't bother tracking/removing; browsers ignore duplicates
  } catch {}

  prefetched.add(url);
};

const FollowingMobile: React.FC = () => {
   const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const { t } = useTranslation();
    const { likedIds, likeCounts, likePost, unlikePost, refreshLikes } = useLikes();
    const [globalMuted, setGlobalMuted] = useState<boolean>(true); // start muted
    const rrLoc = useRouterLocation();
  
    const [likingInProgress, setLikingInProgress] = useState<string | null>(null);
    const toNum = (v: any) => (typeof v === 'number' ? v : Number(v) || 0);
    const [pageIndex, setPageIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
  
    //Loading
    const [isFirstBatchLoading, setIsFirstBatchLoading] = useState(true);

    // ---- Search helpers (same idea as Gallery) ----
  const normText = (s?: string | null) => (s || "").toLowerCase();
  
  const matchesSearch = (post: Post, rawQuery: string): boolean => {
    const q = rawQuery.trim();
    if (!q) return true; // empty search → no filtering
  
    // remove leading '#' so both "#budapest" and "budapest" work
    const needle = q.replace(/^#/, "").toLowerCase();
  
    const haystack: string[] = [];
    const anyPost: any = post;
  
    // Text fields (depends what your API sends – keep or tweak as needed)
    if (anyPost.caption) haystack.push(anyPost.caption);
    if (anyPost.description) haystack.push(anyPost.description);
    if (anyPost.title) haystack.push(anyPost.title);
  
    // Hashtags / tags arrays
    if (Array.isArray(anyPost.hashtags)) haystack.push(...anyPost.hashtags);
    if (Array.isArray(anyPost.tags)) haystack.push(...anyPost.tags);
  
    // Location: let "pest" match "Budapest" city, etc.
    if (post.location?.city) haystack.push(post.location.city);
    if (post.location?.country) haystack.push(post.location.country);
  
    // Substring, case-insensitive match
    return haystack.some((txt) => txt && normText(txt).includes(needle));
  };
  
    // ---------- UI State ----------
    const [feed, setFeed] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [snapReady, setSnapReady] = useState(false);
    const keyOf = (p: Post) => String(p._id ?? p.likeKey);
    const feedRef = useRef<HTMLDivElement | null>(null);
    const resumeSnapshotRef = useRef(false);
    const restoredOnceRef = useRef(false);
    const ignoreScrollRef = useRef(false);
    const didRestoreRef = useRef(false);
    const restoringRef = useRef(false);
    const mergingRef = useRef(false);
    const mountedRef = useRef(false);
  
  
    // ---- Following: smart vs hard reload ----
  const FOLLOWING_HARD_RELOAD_KEY = 'following:hardReload';
  
  const F_FEED   = 'following:feed';
  const didInitialAnchorRef = useRef(false);
  
  const reanchorOnce = () => {
    if (didInitialAnchorRef.current) return;
    const root = feedRef.current;
    if (!root) return;
  
    const anchorId = sessionStorage.getItem('following:anchorId');
    if (!anchorId) { didInitialAnchorRef.current = true; setSnapReady(true); return; }
  
    const el = root.querySelector<HTMLElement>(`[data-post-id="${anchorId}"]`);
    didInitialAnchorRef.current = true;
    if (el) el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
    setSnapReady(true); // enable snap AFTER we’re in place
  };
  const handleOpenMusic = async (entry: MediaEntry) => {
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
      return;
    }
  
    const q = (entry.musicTitle || entry.musicArtist || '').trim();
    if (!q) {
      console.warn('[FollowingFeed.handleOpenMusic] no title/artist on entry', entry);
      return;
    }
  
    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
      if (!list.length) {
        console.warn('[FollowingFeed.handleOpenMusic] no music match for', q);
        return;
      }
  
      navigate(`/musicdetails/${list[0]._id}`);
    } catch (e) {
      console.error('[FollowingFeed.handleOpenMusic] music lookup failed:', e);
    }
  };
  
   
  // split once
  const galleryOnly = useMemo(() => feed.filter(p => p.type === 'media'), [feed]);
  
    // Infinite scroll bookkeeping
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const loadingRef = useRef<boolean>(false);
    const hasMoreRef = useRef<boolean>(true);
  
    // ---------- Following Users ----------
    const [followingUsers, setFollowingUsers] = useState<LiteUser[]>([]);
    const [userBatchIndex, setUserBatchIndex] = useState(0);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
  
    // Per-user offsets for /visitor-postings pagination (round-robin loads)
    const perUserOffsets = useRef<Record<string, number>>({});
  
    // Config knobs
    const USERS_PER_BATCH = 20;       // how many followed users we load per “page”
    const POSTS_PER_USER_PULL = 30;   // how many recent posts per user per batch
  
  // ----- decide once, synchronously
  const hasInitRef = useRef(false);
  if (!hasInitRef.current) {
    const hard = sessionStorage.getItem(FOLLOWING_HARD_RELOAD_KEY) === '1';
    const hasSnap = !!sessionStorage.getItem(F_FEED);
    // If we have a saved snapshot and NOT a hard reload → smart resume
    resumeSnapshotRef.current = !hard && hasSnap;
    hasInitRef.current = true;
  }
  
  const reanchorToSaved = () => {
    const root = feedRef.current;
    if (!root) return;
    const anchorId = sessionStorage.getItem('following:anchorId');
    if (!anchorId) return;
    const el = root.querySelector<HTMLElement>(`[data-post-id="${anchorId}"]`);
    if (el) el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
  };
  
  const handleLike = async (targetKey: string) => {
  const target = String(targetKey);
  if (likingInProgress === target) return;

  setLikingInProgress(target);

  try {
    const newLikes = await likePost(target);

    setFeed(prev =>
      prev.map(p => {
        if (keyOf(p) !== target) return p;

        return {
          ...p,
          likedByUser: true,
          ...(newLikes !== undefined ? { likes: newLikes, totalLikes: newLikes } : {})
        };
      })
    );
  } finally {
    setLikingInProgress(null);
  }
};

const handleUnlike = async (targetKey: string) => {
  const target = String(targetKey);
  if (likingInProgress === target) return;

  setLikingInProgress(target);

  try {
    const newLikes = await unlikePost(target);

    setFeed(prev =>
      prev.map(p => {
        if (keyOf(p) !== target) return p;

        return {
          ...p,
          likedByUser: false,
          ...(newLikes !== undefined ? { likes: newLikes, totalLikes: newLikes } : {})
        };
      })
    );
  } finally {
    setLikingInProgress(null);
  }
};

  
  
  //Like Refresh
  // Refresh likes whenever we enter this page or the logged-in user changes
  useEffect(() => {
    if (!authUser?._id) return;
    refreshLikes();
  }, [authUser?._id, refreshLikes]);
  
  
  useEffect(() => {
    const onPlay = (e: Event) => {
      const el = e.target as HTMLVideoElement | null;
      if (!el || el.tagName !== 'VIDEO') return;
  
      // Pause every other video element on the page
      document.querySelectorAll('video').forEach((v) => {
        if (v !== el) {
          try { v.pause(); } catch {}
        }
      });
    };
  
    // Use capture phase so it catches plays from custom players too
    document.addEventListener('play', onPlay, true);
    return () => document.removeEventListener('play', onPlay, true);
  }, []);
  
  
    // ---------- Initial load: who am I following? ----------
    useEffect(() => {
      const loadFollowing = async () => {
        if (!authUser?._id) return;
        try {
          setLoading(true);      
          const res = await api.get(`/follows/${authUser._id}/following`, {
            params: { offset: 0, limit: 100 },
          });
      
          const rawUsers = Array.isArray(res.data?.users) ? res.data.users : [];
          const followed: LiteUser[] = rawUsers
            .filter((u: any) => u && u._id)
            .map((u: any) => ({ _id: String(u._id), profilename: u.profilename || t('followingFeed.unknownUser'), picture: u.picture || '' }));
      
          setFollowingUsers(followed);
          setUserBatchIndex(0);
          setHasMoreUsers(followed.length > 0);
      
          // 👇 IMPORTANT: only clear feed/reset offsets on HARD loads
          if (!resumeSnapshotRef.current) {
            setFeed([]);
            perUserOffsets.current = {};
          }
        } catch (err: any) {
          console.error('❌ Failed to load following:', err);
        } finally {
          setLoading(false);
        }
      };
      
  
      loadFollowing();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser?._id]);
    
 useEffect(() => {
  if (!feed.length) return;

  setFeed(prev =>
    prev.map(p => {
      const key = keyOf(p);
      const count = likeCounts.get(key);

      return {
        ...p,
        likedByUser: likedIds.has(key),
        likes:
          count != null
            ? Math.max(count, p.likes ?? 0)   // ← critical
            : p.likes,
        totalLikes:
          count != null
            ? Math.max(count, p.totalLikes ?? p.likes ?? 0)
            : p.totalLikes,
      };
    })
  );
}, [likedIds, likeCounts, feed.length]);

  
  const normalizeMixedPost = (raw: any, u: LiteUser): Post => {
    const likeKey = likeKeyFromRow(raw);
    const rawMedia =
      Array.isArray(raw.media) ? raw.media :
      Array.isArray(raw.mediaUrls) ? raw.mediaUrls :
      Array.isArray(raw.attachments) ? raw.attachments :
      Array.isArray(raw.files) ? raw.files :
      raw.videoId ? [{ isHls: true, videoId: raw.videoId }] :
      raw.imageUrl ? [raw.imageUrl] :
      [];
  const toMediaEntry = (m: any): MediaEntry | null => {
  if (m?.isHls === true || m?.videoId || m?.hlsUrl || m?.dashUrl) {
    return {
      isHls: true,
      videoId: m.videoId ?? null,
      hlsUrl: m.hlsUrl ?? m.url ?? null,
      dashUrl: m.dashUrl ?? null,
      posterUrl: m.posterUrl ?? null,
      musicId: m.musicId ?? null,
      musicTitle: m.musicTitle ?? null,
      musicArtist: m.musicArtist ?? null,
    };
  }

  if (typeof m === "string") {
    return { url: toImageUrl(m) };
  }

  if (typeof m?.url === "string") {
    return { url: toImageUrl(m.url) };
  }

  if (typeof m?.src === "string") {
    return { url: toImageUrl(m.src) };
  }

  if (typeof m?.path === "string") {
    return { url: toImageUrl(m.path) };
  }

  return null;
};
  

  const media = rawMedia
    .map(toMediaEntry)
    .filter(Boolean) as MediaEntry[];
  
 for (const m of media) {
  if (m.isHls) {
    const hasPlayableVideo = typeof m.hlsUrl === "string" || typeof m.videoId === "string";
    if (!hasPlayableVideo) {
    }
  } else {
    if (typeof m.url !== "string") {
    }
  }
  }
    const likesCount =
      typeof raw.likes === "number"
        ? raw.likes
        : Array.isArray(raw.likesBy)
        ? raw.likesBy.length
        : 0;
  
  return {
  _id: raw._id || likeKey,   // prefer real DB id
  likeKey,
  type: "media",

  captions: Array.isArray(raw.captions)
    ? raw.captions
    : raw.caption
    ? [raw.caption]
    : [],

  title: raw.title ?? undefined,
  titleColor: raw.titleColor ?? undefined,

  media,

  createdAt: raw.createdAt || new Date().toISOString(),

  location: raw.location
    ? {
        city: raw.location.city || "",
        country: raw.location.country || "",
        countryCode: raw.location.countryCode ?? undefined,
      }
    : undefined,

  likes: likesCount,
  totalLikes: likesCount,

  likedByUser: likedIds.has(likeKey),

  user: u?._id
    ? {
        _id: u._id,
        profilename: u.profilename,
        picture:
          typeof u.picture === "string"
            ? u.picture
            : typeof (u.picture as any)?.path === "string"
            ? toImageUrl((u.picture as any).path)
            : null,
      }
    : null,
};
  };
  
      
    // ---------- Pull a batch of posts from followed users ----------
    const fetchNextBatch = async () => {
    if (loadingRef.current) return;
    if (!hasMoreUsers) return;
  
    loadingRef.current = true;
    try {
      const batch = followingUsers.slice(userBatchIndex, userBatchIndex + USERS_PER_BATCH).filter((u) => u && u._id);
      if (batch.length === 0) {
        hasMoreRef.current = false;
        setHasMoreUsers(false);
        return;
      }
  
      const userCalls = batch.map(async (u) => {
        const currentOffset = perUserOffsets.current[u._id] || 0;
        const res = await api.get(`/visitor-postings/${u._id}`, {
          params: { offset: currentOffset, limit: POSTS_PER_USER_PULL },
        });
  
        const raw: any[] = Array.isArray(res.data) ? res.data : res.data?.posts || [];
        const normalized = raw.map((r) => normalizeMixedPost(r, u));
  
        perUserOffsets.current[u._id] = currentOffset + POSTS_PER_USER_PULL;
        return normalized;
      });
  
      const results = await Promise.all(userCalls);
      const merged = results.flat();
  
      // Freeze scroll reactions during merge
      mergingRef.current = true;
  
setFeed((prev) => {
  if (restoredOnceRef.current) {
    const seen = new Set(prev.map(p => keyOf(p)));
    const toAppend = merged.filter(m => !seen.has(keyOf(m)));
    restoredOnceRef.current = false;
    return [...prev, ...toAppend].slice(-MAX_POSTS_IN_MEMORY);
  }

  const byKey = new Map<string, Post>();

  for (const remote of merged) {
    byKey.set(keyOf(remote), remote);
  }

  for (const local of prev) {
    const k = keyOf(local);
    const remote = byKey.get(k);

    if (!remote) {
      byKey.set(k, local);
      continue;
    }

    const localCount = toNum((local as any).totalLikes ?? local.likes);
    const remoteCount = toNum((remote as any).totalLikes ?? remote.likes);

    byKey.set(k, {
      ...remote,
      ...local,
      likes: Math.max(localCount, remoteCount),
      totalLikes: Math.max(localCount, remoteCount),
      likedByUser: likedIds.has(k),
    });
  }

  return Array.from(byKey.values()).slice(-MAX_POSTS_IN_MEMORY);
});
  
      requestAnimationFrame(() => {
        reanchorToSaved();
        requestAnimationFrame(() => {
          mergingRef.current = false;
        });
      });
  
      const nextIndex = userBatchIndex + USERS_PER_BATCH;
      setUserBatchIndex(nextIndex);
      const moreUsersRemain = nextIndex < followingUsers.length;
      setHasMoreUsers(moreUsersRemain);
      hasMoreRef.current = moreUsersRemain;
    } catch (err) {
      console.error('❌ Failed to fetch following feed batch:', err);
    } finally {
      loadingRef.current = false;
       setIsFirstBatchLoading(false); 
    }
  };
  
    
    
    const itemsToRender = useMemo(() => {
      const base = galleryOnly;
      const hasAnchor = !!sessionStorage.getItem('following:anchorId');
      return hasAnchor ? base : [...base].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }, [galleryOnly]);
    
    // Apply search filter on top of the anchored/sorted list
  const filteredItems = useMemo(
    () => itemsToRender.filter((p) => matchesSearch(p, searchQuery)),
    [itemsToRender, searchQuery]
  );
  
    
  
  const getAnchorId = () => sessionStorage.getItem('following:anchorId') || null;
  
  const getAnchorIndex = () => {
    const id = getAnchorId();
    if (!id) return 0;
    const idx = filteredItems.findIndex((p) => p._id === id);
    return idx >= 0 ? idx : 0;
  };
  
  useLayoutEffect(() => {
    // 1) Scroll to the element (your existing logic)
    reanchorOnce();
  
    // 2) Match the virtual index to the anchored DOM position
    //    so the current slide is considered "active" immediately.
    const idx = getAnchorIndex();
    if (idx !== pageIndex) setPageIndex(idx);
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToRender.map(p => p._id).join('|')]);
  
  
  // Keyboard: swipe one post per key press (Down/Up, PgDn/PgUp, J/K, Space)
  useEffect(() => {
    const el = feedRef.current;
    if (!el) return;
  
    const isTypingTarget = (t: EventTarget | null) => {
      const n = t as HTMLElement | null;
      if (!n) return false;
      const tag = n.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (n as HTMLElement).isContentEditable
      );
    };
  
    const getIndex = () =>
      Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
  
    const scrollToIndex = (i: number) => {
      const max = Math.max(0, filteredItems.length - 1);
      const idx = Math.max(0, Math.min(max, i));
      el.scrollTo({ top: idx * (el.clientHeight || 1), behavior: 'smooth' });
    };
  
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (!filteredItems.length) return;
  
      const idx = getIndex();
  
      // Down-like keys: ArrowDown, PageDown, J/j, Space (unless Shift+Space)
      if (
        e.key === 'ArrowDown' ||
        e.key === 'PageDown' ||
        e.key === 'j' ||
        e.key === 'J' ||
        (e.key === ' ' && !e.shiftKey)
      ) {
        e.preventDefault();
        scrollToIndex(idx + 1);
        return;
      }
  
      // Up-like keys: ArrowUp, PageUp, K/k, Shift+Space
      if (
        e.key === 'ArrowUp' ||
        e.key === 'PageUp' ||
        e.key === 'k' ||
        e.key === 'K' ||
        (e.key === ' ' && e.shiftKey)
      ) {
        e.preventDefault();
        scrollToIndex(idx - 1);
        return;
      }
  
      // Home / End jump
      if (e.key === 'Home') {
        e.preventDefault();
        scrollToIndex(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        scrollToIndex(filteredItems.length - 1);
        return;
      }
    };
  
    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [filteredItems.length]);
  
  
  //Search use Effect
  useEffect(() => {
    // When search text changes, jump back to top of the filtered list
    setPageIndex(0);
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [searchQuery]);
  
  
  
    // optional: persist across reloads
  useEffect(() => {
    const saved = localStorage.getItem('globalMuted');
    if (saved != null) setGlobalMuted(JSON.parse(saved));
  }, []);
  useEffect(() => {
    localStorage.setItem('globalMuted', JSON.stringify(globalMuted));
  }, [globalMuted]);
  
  
  //Loading
  useEffect(() => {
  if (!loading && followingUsers.length === 0) {
    setIsFirstBatchLoading(false);
  }
}, [loading, followingUsers.length]);


  // Prefetch a window of upcoming posts (prev a bit, ahead more) — same as Gallery
  useEffect(() => {
    if (!canPrefetch()) return;
    if (!filteredItems.length) return;
  
    const start = Math.max(0, pageIndex - 2);
    const end = Math.min(filteredItems.length, pageIndex + 1 + PREFETCH_POSTS_AHEAD);
  
    const queue: string[] = [];
  
    for (let i = start; i < end; i++) {
      const post = filteredItems[i];
      if (!post) continue;
  
  const pic = post.user?.picture;
  if (typeof pic === "string" && pic.length > 0) {
    queue.push(pic);               // ✅ already normalized
  }
  
  
  
      const m = post.media || [];
      const N = Math.min(PREFETCH_MEDIA_PER_POST, m.length);
      for (let j = 0; j < N; j++) {
        const entry = m[j] as MediaEntry;
  
  if (entry.isHls && entry.videoId) {
    // ❌ do nothing here — HLS handled by useHlsPrefetch
    continue;
  }
  
  if (entry.isHls) {
    // NEVER prefetch HLS here
    return;
  }
  
  if (typeof entry.url === "string") {
  if (typeof entry.url === "string" && entry.url.length > 0) {
    queue.push(entry.url);
  }
  }
      }
    }
  
    scheduleIdle(() => {
      for (const u of queue) prefetchAsset(u);
      trimImageHandles();
    });
  }, [pageIndex, filteredItems]);
  
  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;
  
    const observer = new IntersectionObserver(
      
      (entries) => {
        if (restoringRef.current || mergingRef.current) return;
        const first = entries[0];
        if (first.isIntersecting && !loadingRef.current && hasMoreRef.current) {
          fetchNextBatch();
        }
      },
      { root, threshold: 0.1, rootMargin: '200px 0px' } // preload sooner
    );
  
    const el = bottomRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); observer.disconnect(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followingUsers, userBatchIndex, hasMoreUsers]);
  
  
  //Loading
useEffect((): (() => void) => {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
}, []);



    // Play video on slide change
  
    useEffect(() => {
      if (followingUsers.length && feed.length === 0 && !loadingRef.current) {
        fetchNextBatch();
      }
    }, [followingUsers]);
    
    useEffect(() => {
      if (mountedRef.current) return;
      mountedRef.current = true;
    
      // Only do your initial “resumeSnapshotRef / fetchNextBatch” kick here.
      if (!resumeSnapshotRef.current && followingUsers.length && feed.length === 0 && !loadingRef.current) {
        fetchNextBatch();
      }
    }, [followingUsers]); // keep deps; the guard prevents re-run
  
    useEffect(() => {
      const hard = sessionStorage.getItem(FOLLOWING_HARD_RELOAD_KEY) === '1';
      const hasSnap = !!sessionStorage.getItem(F_FEED);
    
      if (hard) {
        // clear hard flag + anchor and reset local pagination state
        sessionStorage.removeItem('following:anchorId');
        sessionStorage.removeItem(FOLLOWING_HARD_RELOAD_KEY);
    
        setFeed([]);
        setUserBatchIndex(0);
        setHasMoreUsers(true);
        perUserOffsets.current = {};
        didRestoreRef.current = false;
    
        // re-enable the infinite-scroll machinery
        hasMoreRef.current = true;
        loadingRef.current = false;
    
        // 🔑 if we already know who we follow, start fetching NOW
        if (followingUsers.length > 0) {
          fetchNextBatch();
        }
        return; // done with hard path
      }
    
      // (optional) snapshot branch — keep as-is or remove if you no longer use snapshots
      if (hasSnap && !didRestoreRef.current) {
        didRestoreRef.current = true;
        restoringRef.current = true;
        ignoreScrollRef.current = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            ignoreScrollRef.current = false;
            restoringRef.current = false;
          });
        });
        restoredOnceRef.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rrLoc.search, followingUsers.length]);
    
    
    
    useEffect(() => {
      if (!resumeSnapshotRef.current) return;
      if (followingUsers.length === 0) return;
      fetchNextBatch();
      resumeSnapshotRef.current = false; // run once
    }, [followingUsers]);
    
    
    // Decide hard vs smart on mount
    useEffect(() => {
      if (didRestoreRef.current) return;       // ⬅️ prevent duplicate restore
      const hard = sessionStorage.getItem(FOLLOWING_HARD_RELOAD_KEY) === '1';
      const hasSnap = !!sessionStorage.getItem(F_FEED);
    
      resumeSnapshotRef.current = !hard && hasSnap;
    
      if (hard) {
        sessionStorage.removeItem('following:anchorId');
        sessionStorage.removeItem(FOLLOWING_HARD_RELOAD_KEY);
        setFeed([]);
        setUserBatchIndex(0);
        setHasMoreUsers(true);
        perUserOffsets.current = {};
      } else if (hasSnap) {
        didRestoreRef.current = true;          
        ignoreScrollRef.current = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            ignoreScrollRef.current = false;
          });
        });
        restoredOnceRef.current = true;
      }
    }, []);                                    
    
  useEffect(() => {
    return () => {
    };
  }, [pageIndex, itemsToRender]);
  
  
  useEffect(() => {
    // one-time warm-up when we first set pageIndex from the anchor
    if (!didInitialAnchorRef.current) return;
    if (!canPrefetch() || !filteredItems.length) return;
  
    const start = Math.max(0, pageIndex - 2);
    const end   = Math.min(filteredItems.length, pageIndex + 1 + 5);
    const queue: string[] = [];
  
    for (let i = start; i < end; i++) {
      const post = filteredItems[i];
      if (!post) continue;
  const pic = post.user?.picture;
  if (typeof pic === "string" && pic.length > 0) {
    queue.push(pic);              
  }
  
  const m = post.media || [];
  const N = Math.min(3, m.length);
  for (let j = 0; j < N; j++) {
    const entry = m[j] as MediaEntry;
  
    if (entry.isHls && entry.videoId) {
      // HLS handled by useHlsPrefetch
      continue;
    }
  
  if (typeof entry.url === "string" && entry.url.length > 0) {
    queue.push(entry.url);
       }
     }
    }
  
    scheduleIdle(() => {
      for (const u of queue) prefetchAsset(u);
      trimImageHandles();
    });
  }, [pageIndex, filteredItems.length]);
  


return (
  <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

    {/* FEED */}
    <div
      ref={feedRef}
      onScroll={(e) => {
        if (!didInitialAnchorRef.current) return;
        if (ignoreScrollRef.current || restoringRef.current || mergingRef.current) return;

        const el = e.currentTarget as HTMLDivElement;
        const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));

        if (idx !== pageIndex) setPageIndex(idx);

        const anchorId = filteredItems[idx]?._id;
        if (anchorId) sessionStorage.setItem('following:anchorId', anchorId);
      }}
      style={{
        flexGrow: 1,
        overflowY: 'auto',
        height: '100dvh',
        backgroundColor: '#000',

        scrollSnapType: snapReady ? 'y mandatory' : 'none',
        touchAction: 'pan-y',
        overscrollBehaviorY: 'contain',
      }}
    >
     {isFirstBatchLoading ? (
  <div
    style={{
      height: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 14,
      color: '#bbb',
      fontSize: 16,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

    <div>
      {t('followingFeed.loading')}
    </div>
  </div>
) : filteredItems.length > 0 ? (

filteredItems.map((post, idx) => {
  const isActive = idx === pageIndex;
  const inWindow = idx >= pageIndex - 1 && idx <= pageIndex + 1;
  const shouldRender =
    idx >= pageIndex - WINDOW_BEFORE &&
    idx <= pageIndex + WINDOW_AFTER;

  if (!shouldRender) {
    return (
      <section
        key={post._id}
        data-post-id={post._id}
        style={{
          height: '100dvh',
          minHeight: '100dvh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          margin: 0,
          padding: 0,
        }}
      />
    );
  }

  return (
    <section
      key={post._id}
      data-post-id={post._id}
      style={{
        height: '100dvh',
        minHeight: '100dvh',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        margin: 0,
        padding: 0,
      }}
    >
      <PostSection
        post={post}
        isActive={isActive}
        inWindow={inWindow}
        globalMuted={globalMuted}
        navigate={navigate}
        onLike={() => handleLike(post.likeKey || post._id)}
        onUnlike={() => handleUnlike(post.likeKey || post._id)}
        currentUserId={authUser?._id}
        onMusicClick={handleOpenMusic}
      />
    </section>
  );
})
      ) : (
        <p style={{ color: '#aaa', padding: 16 }}>
          {t('followingFeed.noPosts')} 🤷‍♀️
        </p>
      )}

      {/* infinite scroll sentinel */}
      <div ref={bottomRef} style={{ height: 20 }} />
    </div>

    {/* MOBILE MENU BUTTON */}
    <div
      onClick={() => navigate('/menu')}
      style={{
        position: 'absolute',
        top: 14,
        right: 17,
        padding: 10,
        cursor: 'pointer',
        zIndex: 10,
      }}
    >
      <FaBars style={{ color: '#bbb', fontSize: 19 }} />
    </div>
  </div>
);
};

export default FollowingMobile;